import React, { useState, useEffect, useContext } from 'react';
import ManageProjectsTable from './ManageProjectsTable';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import useApi from 'hooks/useApi';
import {
  deleteProject,
  getCountries,
  getProjectLists,
  getStates,
  startProcessing
} from 'api/endpoints/ManagementServices';
import { isEmpty } from 'lodash';
import { get } from 'lodash';
import FalconCardHeader from 'components/common/FalconCardHeader';
import IconButton from 'components/common/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AppContext from 'context/Context';
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import CustomSpinner from 'components/common/CustomSpinner';
import { toast } from 'react-toastify';
import CustomSelect from 'components/common/CustomSelect';
import { jwtDecode } from 'jwt-decode';
import { getTenantFromToken } from 'utils/routeUtils';

const ManageProjects = () => {
  const {
    config: { isRTL, isDark }
  } = useContext(AppContext);
  const isDemoDomain = window.location.hostname === 'envira.farmsio.com';

  const tenant = getTenantFromToken();

  const [loading, setLoading] = useState(true);
  const [projectLists, setProjectLists] = useState([]);
  const [lastSearchData, setLastSearchData] = useState({
    tenant: tenant,
    country: '',
    state: '',
    cluster: ''
  });

  const [projectName, setProjectName] = useState('');
  const [country, setCountry] = useState(null);
  const [countriesList, setCountriesList] = useState([
    { label: 'Loading.....', isDisabled: true }
  ]);
  const [state, setState] = useState(null);
  const [statesList, setStatesList] = useState([]);

  const getProjectListsAPI = useApi(getProjectLists);
  const getCountriesAPI = useApi(getCountries);
  const getStatesAPI = useApi(getStates);
  const deletePorjectAPI = useApi(deleteProject);
  const startProcessingAPI = useApi(startProcessing);

  useEffect(() => {
    getCountriesAPI.request();
  }, []);

  useEffect(() => {
    if (country?.value) {
      setStatesList([{ label: 'Loading.....', isDisabled: true }]);
      getStatesAPI.request(country.value);
    }
  }, [country]);

  useEffect(() => {
    const countries = get(getCountriesAPI, 'data.results', []);

    const countriesList = countries.map(country => ({
      value: country.id,
      label: country.name
    }));

    setCountriesList(countriesList);
  }, [getCountriesAPI.data]);

  useEffect(() => {
    const states = get(getStatesAPI, 'data.results', []);

    const statesList = states.map(state => ({
      value: state.id,
      label: state.name
    }));

    !isEmpty(statesList) && setStatesList(statesList);
  }, [getStatesAPI.data]);

  useEffect(() => {
    getProjectListsAPI.request({
      tenant: tenant
    });
  }, []);

  useEffect(() => {
    if (!isEmpty(getProjectListsAPI.data)) {
      const projectLists = get(getProjectListsAPI.data, 'results', []);
      setProjectLists(projectLists);
    }
  }, [getProjectListsAPI.data]);

  useEffect(() => {
    if (!isEmpty(getProjectListsAPI.error)) {
      setProjectLists([]);
    }
  }, [getProjectListsAPI.error]);

  // Delele Project API handling
  useEffect(() => {
    if (!isEmpty(deletePorjectAPI.data)) {
      console.log(deletePorjectAPI.data);
      toast.success('Project deleted successfully');
      getProjectListsAPI.request(lastSearchData);
    }
  }, [deletePorjectAPI.data]);

  useEffect(() => {
    if (!isEmpty(deletePorjectAPI.error)) {
      console.log(deletePorjectAPI.error);
      toast.error('Failed to delete the project. Try again later');
    }
  }, [deletePorjectAPI.error]);

  // Start Process API handling
  useEffect(() => {
    if (!isEmpty(startProcessingAPI.data)) {
      console.log(startProcessingAPI.data);
      toast.success('Requested for processing successfully');
      getProjectListsAPI.request(lastSearchData);
    }
  }, [startProcessingAPI.data]);

  useEffect(() => {
    if (!isEmpty(startProcessingAPI.error)) {
      console.log(startProcessingAPI.error);
      toast.error('Failed to start processing. Try again later');
    }
  }, [startProcessingAPI.error]);

  const handleSearch = event => {
    event.preventDefault();

    if (!isEmpty(projectName) && isEmpty(state)) {
      return;
    }

    const searchData = {
      tenant: tenant,
      country: country?.label || '',
      state: state?.label || '',
      cluster: projectName
    };
    getProjectListsAPI.request(searchData);
    setLastSearchData(searchData);
  };

  useEffect(() => {
    if (isEmpty(country)) {
      setState(null);
      setProjectName('');
      return;
    }
    if (isEmpty(state)) {
      setProjectName('');
    }
  }, [country, state]);

  const handleReload = () => {
    getProjectListsAPI.request(lastSearchData);
  };

  return (
    <Container fluid className="p-0">
      <Row className="mb-3">
        <Col xs={12}>
          <Form onSubmit={handleSearch} className="mt-1">
            <Row className="g-3 g-lg-4">
              <Col lg={3}>
                <CustomSelect
                  options={countriesList}
                  isClearable={true}
                  placeholder="Select Country"
                  classNamePrefix="react-select"
                  value={country}
                  onChange={value => {
                    setCountry(value);
                    setState(null);
                  }}
                />
              </Col>
              <Col lg={3}>
                <CustomSelect
                  options={statesList}
                  isDisabled={isEmpty(country)}
                  isClearable={true}
                  placeholder="Select State"
                  classNamePrefix="react-select"
                  value={state}
                  onChange={value => setState(value)}
                />
              </Col>
              <Col lg={3}>
                <Form.Control
                  type="text"
                  disabled={isEmpty(country) || isEmpty(state)}
                  className={!isDark && 'bg-white'}
                  placeholder="Project Name"
                  value={projectName}
                  onChange={e => setProjectName(e.target.value)}
                />
              </Col>
              <Col className="d-flex justify-content-end">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card className="mt-2">
            <Card.Body>
              <Row>
                <Col className=" pb-3">
                  <FalconCardHeader
                    title="Project Details"
                    titleTag="h5"
                    className="p-0 my-auto"
                    endEl={
                      <div className="d-flex justify-content-end my-auto">
                        <div
                          className="px-2 my-auto theme-control-toggle"
                          onClick={handleReload}
                        >
                          <OverlayTrigger
                            key="left"
                            placement={isRTL ? 'bottom' : 'left'}
                            overlay={<Tooltip id="ThemeColor">Reload</Tooltip>}
                          >
                            <div className="theme-control-toggle-label">
                              <FontAwesomeIcon icon={faRedo} className="fs-0" />
                            </div>
                          </OverlayTrigger>
                        </div>
                        {!isDemoDomain && (
                          <Link to="new" className="text-decoration-none">
                            <IconButton
                              className="d-none d-lg-block"
                              icon="plus"
                              variant="falcon-default"
                            >
                              &nbsp; Create New Project
                            </IconButton>
                            <IconButton
                              className="d-lg-none px-2"
                              icon="plus"
                              variant="falcon-default"
                            >
                              &nbsp;New
                            </IconButton>
                          </Link>
                        )}
                      </div>
                    }
                  />
                </Col>
              </Row>
              {getProjectListsAPI.loading ? (
                <CustomSpinner />
              ) : isEmpty(projectLists) ? (
                <p className="text-center">No Projects Found</p>
              ) : (
                <ManageProjectsTable
                  data={projectLists}
                  deletePorjectAPI={deletePorjectAPI}
                  startProcessingAPI={startProcessingAPI}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ManageProjects;
